import { useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  Radio,
  Divider,
  Button,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Card,
  CardContent,
  Tooltip,
  Autocomplete,
  Alert,
  IconButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import HomeIcon from "@mui/icons-material/Home";
import EditIcon from "@mui/icons-material/Edit";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useAdminContext } from "../../hooks/UseAdminContext";
import ReviewGettingStartedSection from "./ReviewApplication";
import { visa_application } from "./SidebarData";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneInput from "react-phone-number-validation";
import {
  add_update_user_form,
  detail_user_form,
  one_time_payment_with_stripe_api,
  _applicant_audit_logs,
  detail_addon_form,
  add_update_addon_form,
  get_client_fees_api,
} from "../../DAL/form/userForm";
import momenttz from "moment-timezone";
import codes from "country-calling-code";
import SignatureCanvas from "react-signature-canvas";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  image_delete_on_s3_api,
  image_upload_on_s3_api,
} from "../../DAL/auth/auth";
import { s3baseUri } from "../../config/config";
import { makeStyles } from "@mui/styles";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ImageIcon from "@mui/icons-material/Image";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import moment from "moment/moment";
import { isValidPhoneNumber } from "libphonenumber-js";
import { _get_documents_api } from "../../DAL/Login/Login";
import UpdateAddonDialog from "../dialogs/UpdateAddonDialog";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

export const VisaApplication = () => {
  const classes = useStyles();
  const { addon_id, section, sub_section } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery("(max-width:600px)");
  const {
    selectedQuestion,
    setSelectedQuestion,
    sectionList,
    setSectionList,
    setAddonCaseNumber,
    hasUnsavedChanges,
    setHasUnsavedChanges,
  } = useAdminContext();
  const [initialLoading, setInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState("");
  const signatureRefs = useRef({});
  const [isPaid, setIsPaid] = useState("");
  const [signaturePreviews, setSignaturePreviews] = useState({});
  const [signedFields, setSignedFields] = useState({});
  const [isSkipped, setIsSkipped] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [checkStatus, setCheckStatus] = useState(false);
  const [addonInfo, setAddonInfo] = useState(null);
  const [feesData, setFeesData] = useState(null);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [openAddonDialog, setOpenAddonDialog] = useState(false);
  const [primaryHousingInfo, setPrimaryHousingInfo] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll back to top of the page - shamroz
    // setHasUnsavedChanges(false);
  }, [location.pathname]);
  // Accidental refresh alert - shamroz
  // useEffect(() => {
  //   const handleBeforeUnload = (event) => {
  //     if (hasUnsavedChanges) {
  //       event.preventDefault();
  //       event.returnValue = "";
  //     }
  //   };
  //   window.addEventListener("beforeunload", handleBeforeUnload);
  //   return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  // }, [hasUnsavedChanges]);
  // // =================================

  const pathname = location?.pathname;
  const isAddon = pathname?.includes("/visa/addon/");

  const handleFetchDetail = async () => {
    let result;
    let localPrimaryHousingInfo = null;

    if (isAddon && addon_id) {
      result = await detail_addon_form(addon_id);
      // --- HOUSING START ---
      const defaultUserResult = await detail_user_form();
      if (defaultUserResult.code === 200) {
        const hSec = defaultUserResult.form?.form?.find(
          (s) => s.value === "housing_option"
        );
        if (hSec) {
          localPrimaryHousingInfo = {};
          hSec.questions.forEach((q) => {
            localPrimaryHousingInfo[q.created_for] = q.answer;
          });
          setPrimaryHousingInfo(localPrimaryHousingInfo);
        }
      }
      // --- HOUSING END ---
    } else {
      result = await detail_user_form();
    }

    if (result.code === 200) {
      if (
        result?.form?.form_status === "rejected" ||
        result?.form?.form_status === "approved"
      ) {
        setCheckStatus(true);
      }
      const formData = result?.form?.form || [];
      setIsPaid(result?.form?.initial_payment_status);
      if (isAddon) {
        setAddonCaseNumber(result?.form?.case_number);
      } else {
        setAddonCaseNumber(null);
      }
      setAddonInfo(result?.form?.addon_info || null);
      // setSectionList(formData); // without housing option for addons
      const mergedForm = visa_application.map((templateSection) => {
        const existingSection = formData.find(
          (s) => s.value === templateSection.value
        );
        let section = existingSection
          ? JSON.parse(JSON.stringify(existingSection))
          : JSON.parse(JSON.stringify(templateSection));

        // --- HOUSING START ---
        if (
          isAddon &&
          section.value === "housing_option" &&
          localPrimaryHousingInfo
        ) {
          section.questions = section.questions.map((q) => {
            if (localPrimaryHousingInfo[q.created_for] !== undefined) {
              return { ...q, answer: localPrimaryHousingInfo[q.created_for] };
            }
            return q;
          });
          // mark as completed for addon
          section.is_completed = true;
        }
        // --- HOUSING END ---

        return section;
      });

      setSectionList(mergedForm);
      const pathSegments = pathname.split("/");

      let sectionValue;
      let subSectionValue;

      if (isAddon) {
        sectionValue = pathSegments[4];
        subSectionValue = pathSegments[5];
      } else {
        sectionValue = pathSegments[2];
        subSectionValue = pathSegments[3];
      }

      let found = null;

      for (let item of mergedForm) {
        if (item.has_sub_sections && Array.isArray(item.sub_sections)) {
          found = item.sub_sections.find(
            (sub) => sub.value === subSectionValue
          );
          if (found) break;
        } else {
          if (item.value === sectionValue) {
            found = item;
            break;
          }
        }
      }

      if (found) {
        setSelectedQuestion(found);
      }

      const feesResult = await get_client_fees_api();
      if (feesResult.code === 200) {
        setFeesData(feesResult.client_fees);
      }

      setInitialLoading(false);
      return formData;
    } else {
      setInitialLoading(false);
      return null;
    }
  };

  const getDocuments = async () => {
    const result = await _get_documents_api();
    if (result.code === 200) {
      const backgroundFiles = result?.general_settings?.background_files || [];
      setDocumentFiles(backgroundFiles);

      const viewFileQuestions = backgroundFiles.map((doc, index) => ({
        question_statement: doc?.name,
        question_type: "view_file",
        answer: doc?.path,
        is_required: false,
        created_for: "bg_section_1",
        question_id: `BG${index + 1}`,
      }));

      setSectionList((prevList) => {
        return prevList.map((section) => {
          if (section.value === "background_check_disclosures") {
            const otherQuestions = (section.questions || []).filter(
              (q) => q.question_type !== "view_file"
            );
            return {
              ...section,
              questions: [...viewFileQuestions, ...otherQuestions],
            };
          }
          return section;
        });
      });

      setSelectedQuestion((prev) => {
        if (prev?.value === "background_check_disclosures") {
          const otherQuestions = (prev.questions || []).filter(
            (q) => q.question_type !== "view_file"
          );
          return {
            ...prev,
            questions: [...viewFileQuestions, ...otherQuestions],
          };
        }
        return prev;
      });
    } else {
      console.error(result.message);
    }
  };

  const handleSetSelectedQuestion = (formData) => {
    const pathSegments = pathname.split("/");
    const sectionValue = pathSegments[2];
    const subSectionValue = pathSegments[3];

    let found = null;

    for (let item of formData) {
      if (item.has_sub_sections && Array.isArray(item.sub_sections)) {
        found = item.sub_sections.find((sub) => sub.value === subSectionValue);
        if (found) break;
      } else {
        if (item.value === sectionValue) {
          found = item;
          break;
        }
      }
    }

    if (found) {
      setSelectedQuestion(found);
    }
  };

  const handleChangeCheckBox = (idx, value) => {
    const temArray = selectedQuestion?.has_sub_sections
      ? [...selectedQuestion?.sub_sections]
      : [...selectedQuestion?.questions];

    let item = { ...temArray[idx] };

    if (item.question_type === "checkbox") {
      let currentAnswers = Array.isArray(item.answer) ? [...item.answer] : [];

      if (currentAnswers.includes(value)) {
        currentAnswers = currentAnswers.filter((v) => v !== value);
      } else {
        currentAnswers.push(value);
      }

      item.answer = currentAnswers;
    } else {
      item.answer = value;
    }

    temArray[idx] = item;

    const updatedSelectedQuestion = { ...selectedQuestion };

    if (selectedQuestion?.has_sub_sections) {
      updatedSelectedQuestion.sub_sections = temArray;
    } else {
      updatedSelectedQuestion.questions = temArray;
    }

    setSelectedQuestion(updatedSelectedQuestion);
    setHasUnsavedChanges(true);
  };

  const handleChangeDropdown = (idx, value) => {
    const updatedQuestion = { ...selectedQuestion };
    if (updatedQuestion.has_sub_sections) {
      updatedQuestion.questions[idx].answer = value;
    } else {
      updatedQuestion.questions[idx].answer = value;
    }

    setSelectedQuestion(updatedQuestion);
    setHasUnsavedChanges(true);
  };

  const handleSignatureChange = async (fieldName, idx) => {
    const signatureCanvas = signatureRefs[fieldName];
    if (!signatureCanvas || signatureCanvas.isEmpty()) {
      enqueueSnackbar("please provide your signature before saving.", {
        variant: "error",
      });
      return;
    }

    const dataURL = signatureCanvas.getCanvas().toDataURL("image/png");

    try {
      const blob = await fetch(dataURL).then((res) => res.blob());

      const file = new File([blob], `signature_${Date.now()}.png`, {
        type: "image/png",
      });

      const formData = new FormData();
      formData.append("file", file);
      const updatedQuestion = { ...selectedQuestion };
      setSignatureLoading(true);
      const res = await image_upload_on_s3_api(formData);

      if (res.code === 200) {
        setSignaturePreviews((prev) => ({
          ...prev,
          [fieldName]: dataURL,
        }));
        if (updatedQuestion.has_sub_sections) {
          updatedQuestion.questions[idx].answer = res.path;
        } else {
          updatedQuestion.questions[idx].answer = res.path;
        }
        setSelectedQuestion(updatedQuestion);

        setSignatureLoading(false);
      }
    } catch (error) {
      enqueueSnackbar("Failed to upload signature", { variant: "error" });
      setSignatureLoading(false);
    }
  };

  const handleChangeClear = (idx) => {
    const updatedQuestion = { ...selectedQuestion };
    if (updatedQuestion.has_sub_sections) {
      updatedQuestion.questions[idx].answer = "";
    } else {
      updatedQuestion.questions[idx].answer = "";
    }
    setSelectedQuestion(updatedQuestion);
    setSignedFields({});
    setHasUnsavedChanges(true);
  };

  const toSlug = (str) =>
    str
      ?.toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^\w_]/g, "");

  const getNextStep = () => {
    const baseRoute = isAddon ? `/visa/addon/${addon_id}` : "/visa";

    const currentSectionIndex = sectionList.findIndex(
      (s) => s.value === section
    );
    if (currentSectionIndex === -1) return null;
    const sectionData = sectionList[currentSectionIndex];

    if (sectionData.has_sub_sections) {
      const currentSubIndex = sectionData.sub_sections.findIndex(
        (s) => toSlug(s.title) === sub_section
      );

      const nextSub = sectionData.sub_sections[currentSubIndex + 1];
      if (nextSub) {
        return {
          path: `${baseRoute}/${section}/${toSlug(nextSub.title)}`,
          selectedQuestion: nextSub,
        };
      }
    }

    const nextSection = sectionList.slice(currentSectionIndex + 1).find(s => !s.hide_section);
    if (!nextSection) return null;

    if (nextSection.has_sub_sections && nextSection.sub_sections.length > 0) {
      const firstSub = nextSection.sub_sections[0];
      return {
        path: `${baseRoute}/${nextSection.value}/${toSlug(firstSub.title)}`,
        selectedQuestion: firstSub,
      };
    } else {
      return {
        path: `${baseRoute}/${nextSection.value}`,
        selectedQuestion: nextSection,
      };
    }
  };

  const handlePayment = async () => {
    if (addon_id) {
      navigate(`/payments/${addon_id}`);
    } else {
      navigate("/payments");
    }
  };

  const handleActivityLog = async (id) => {
    console.log(selectedQuestion, "selected_question");
    const actionType = selectedQuestion?.is_completed
      ? "PAGE_UPDATE"
      : "PAGE_SAVE";
    const req_data = {
      application_id: id,
      action_type: actionType,
      module_info: {
        value: selectedQuestion?.value,
        is_completed: true,
        questions: (selectedQuestion?.questions || []).map((q) => ({
          question_statement: q?.question_statement,
          question_type: q?.question_type,
          options: q?.options || [],
          answer: q?.answer,
          is_required: q?.is_required,
          created_for: q?.created_for,
          question_id: q?.question_id,
        })),
      },
    };
    const result = await _applicant_audit_logs(req_data);
    console.log(result);
  };

  const handleSave = async (e, country, action) => {
    e.preventDefault();

    const questions = selectedQuestion?.questions || [];
    const newErrors = {};

    let isDataMissing = false;
    let isSignMissingPhone = false;
    let isSignMissing = false;
    let isSignNotSaved = false;
    let hasInvalidPhone = false;

    // Get housing_need answer to skip validation for hidden housing fields
    const housing_option_answer = selectedQuestion?.value === 'housing_option'
      ? questions.find(question => question.created_for === 'housing_need')?.answer
      : null;

    questions.forEach((q, idx) => {
      if (q?.is_required) {

        // Skip validation for housing detail fields when housing_need is not "yes"
        if (
          selectedQuestion?.value === 'housing_option' &&
          ['housing_category', 'housing_type', 'preferred_location', 'additional_requirement'].includes(q?.created_for) &&
          housing_option_answer !== 'yes'
        ) {
          return;
        }

        if (
          q?.question_type === "file" &&
          q?.created_for === "dcpj_data_page" &&
          !country
        ) {
          return;
        }

        const isEmpty =
          !q?.answer || q?.answer === "" || q?.answer.length === 0;

        if (q?.question_type === "date") {
          if (isEmpty) {
            newErrors[idx] = "Please select a valid date.";
            isDataMissing = true;
          }
        } else if (
          q?.question_type === "checkbox" ||
          q?.question_type === "file"
        ) {
          if (isEmpty) {
            newErrors[idx] = "This field is required.";
            isDataMissing = true;
          }
        } else if (
          q?.question_type === "radio" ||
          q?.question_type === "dropdown"
        ) {
          if (isEmpty) {
            newErrors[idx] = "This field is required.";
            isDataMissing = true;
          }
        } else if (q?.question_type === "boxwithcheck") {
          if (!q?.answer) {
            newErrors[idx] = "This field is required.";
            isDataMissing = true;
          }
        } else if (q?.question_type === "electronic_sign") {
          if (!q?.answer && signedFields[q?.created_for]) {
            isSignNotSaved = true;
          } else if (!q?.answer) {
            isSignMissingPhone = true;
          }
        } else if (
          q?.created_for === "mobile_number" ||
          q?.created_for === "phone_number"
        ) {
          const isValidNumberCheck = isValidPhoneNumber(q?.answer);
          if (!isValidNumberCheck) {
            hasInvalidPhone = true;
          }
        }
      }
    });

    if (hasInvalidPhone) {
      enqueueSnackbar("Please enter a valid phone number.", {
        variant: "error",
      });
      return;
    }

    if (
      Object.keys(newErrors).length > 0 ||
      isSignMissing ||
      isSignNotSaved ||
      isSignMissingPhone
    ) {
      if (isDataMissing) {
        enqueueSnackbar("Please enter data in all mandatory fields.", {
          variant: "error",
        });
      } else if (isSignMissingPhone || isSignMissing) {
        enqueueSnackbar("Please sign your application.", {
          variant: "error",
        });
      } else if (isSignNotSaved) {
        enqueueSnackbar("Please save your signature to proceed.", {
          variant: "error",
        });
      }
      return;
    }

    let processedQuestion = { ...selectedQuestion };

    const updatedData = sectionList.map((section) => {
      if (section.has_sub_sections) {
        const updatedSubSections = section.sub_sections.map((sub) => {
          if (sub.value === processedQuestion?.value) {
            return {
              ...processedQuestion,
              is_completed: true,
            };
          }
          return sub;
        });

        const allSubCompleted = updatedSubSections.every((s) => s.is_completed);
        return {
          ...section,
          sub_sections: updatedSubSections,
          is_completed: allSubCompleted,
        };
      } else {
        if (section.value === processedQuestion?.value) {
          return {
            ...processedQuestion,
            is_completed: true,
          };
        }
        return section;
      }
    });

    let postData = {
      form: updatedData,
    };

    console.log(updatedData, "updatedData")
    console.log(postData, "postData")

    if (action) {
      setIsSkipped(true);
    } else {
      setIsLoading(true);
    }

    let result;

    if (isAddon && addon_id) {
      result = await add_update_addon_form({
        addon_info: addonInfo,
        form: updatedData,
        addon_form_id: addon_id,
      });
    } else {
      result = await add_update_user_form(postData);
    }

    if (result.code === 200) {
      const savedForm = result.user_form || result.addon_form || result.form;
      // ========== Save activity log ========== //
      await handleActivityLog(savedForm._id);
      // =========== ============== =========== //
      const next = getNextStep();

      if (next) {
        setSelectedQuestion(next.selectedQuestion);
        navigate(next.path);
      }

      // Merge with template to ensure new sections exist after save
      const updatedFormData = savedForm?.form || [];
      const mergedSavedForm = visa_application.map((templateSection) => {
        const existingSection = updatedFormData.find(
          (s) => s.value === templateSection.value
        );
        let section = existingSection
          ? JSON.parse(JSON.stringify(existingSection))
          : JSON.parse(JSON.stringify(templateSection));

        // --- HOUSING START ---
        if (isAddon && section.value === "housing_option" && primaryHousingInfo) {
          section.questions = section.questions.map((q) => {
            if (primaryHousingInfo[q.created_for] !== undefined) {
              return { ...q, answer: primaryHousingInfo[q.created_for] };
            }
            return q;
          });
          // mark as completed for addon
          section.is_completed = true;
        }
        // --- HOUSING END ---

        return section;
      });

      setSectionList(mergedSavedForm);

      const incompleteSections = mergedSavedForm.filter(
        (section) => section?.is_completed !== true
      );

      if (
        incompleteSections.length > 0 &&
        selectedQuestion?.value === "processing_fee_and_submission"
      ) {
        const firstIncomplete = incompleteSections[0];
        enqueueSnackbar(
          `Please complete all required fields in: ${firstIncomplete.title}`,
          { variant: "error" }
        );
      } else {
        enqueueSnackbar("Your information has been saved successfully", {
          variant: "success",
        });
      }

      if (
        incompleteSections.length === 0 &&
        selectedQuestion?.value === "processing_fee_and_submission" &&
        isPaid !== "paid"
      ) {
        handlePayment();
      }

      if (
        selectedQuestion?.value === "processing_fee_and_submission" &&
        isPaid === "paid"
      ) {
        navigate("/draft-page");
      }
      for (const path of deletedFiles) {
        await image_delete_on_s3_api({ path });
      }
      setDeletedFiles([]);
      setIsSkipped(false);
      setIsLoading(false);
      setHasUnsavedChanges(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsSkipped(false);
      setIsLoading(false);
    }
  };

  const removeFile = (q, index) => {
    const fileToRemove = q?.answer?.[index];
    if (!fileToRemove?.path) return;

    setDeletedFiles((prev) => [...prev, fileToRemove.path]);

    setSelectedQuestion((prev) => {
      const updatedQuestions = prev.questions.map((question) => {
        if (question.created_for === q.created_for) {
          const newAnswer = [...(question.answer || [])];
          newAnswer.splice(index, 1);
          return { ...question, answer: newAnswer };
        }
        return question;
      });
      return { ...prev, questions: updatedQuestions };
    });
    setHasUnsavedChanges(true);
  };

  const handleDateChange = (newValue, q, idx) => {
    const updatedQuestion = { ...selectedQuestion };
    const formattedDate = newValue ? moment(newValue).format("YYYY-MM-DD") : "";
    const today = new Date();

    if (!newValue) {
      return;
    }

    const selectedDate = new Date(newValue);

    if (q?.created_for === "date_of_birth") {
      const dob = selectedDate;
      const age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      const dayDiff = today.getDate() - dob.getDate();

      const is18OrOlder =
        age > 18 ||
        (age === 18 && (monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0)));

      if (dob > today) {
        enqueueSnackbar("Future date not allowed.", { variant: "error" });
        return;
      } else if (!is18OrOlder) {
        enqueueSnackbar("You must be at least 18 years old.", {
          variant: "error",
        });
        return;
      }
    }

    if (q?.created_for === "date_of_issue") {
      if (selectedDate > today) {
        enqueueSnackbar("Date of Issue cannot be in the future.", {
          variant: "error",
        });
        return;
      }
    }

    if (q?.created_for === "date_of_expiration") {
      if (selectedDate <= today) {
        enqueueSnackbar("Date of Expiration must be a future date.", {
          variant: "error",
        });
        return;
      }
    }

    if (updatedQuestion.has_sub_sections) {
      updatedQuestion.questions[idx].answer = formattedDate;
    } else {
      updatedQuestion.questions[idx].answer = formattedDate;
    }

    setSelectedQuestion(updatedQuestion);
    setHasUnsavedChanges(true);
  };

  const handleGetCountry = () => {
    const userTimezone = momenttz.tz.guess();
    let country = momenttz.tz.zone(userTimezone).countries();
    if (country.length > 0) {
      country = country[0];
      return country;
    }
  };

  const country = handleGetCountry();
  let country_obj = codes.find((item) => item?.isoCode2 == country);
  const countryCode = country_obj?.countryCodes[0];

  const renderQuestion = (q, idx) => {
    const isRequired = q?.is_required;
    const questionText = isRequired
      ? q?.question_statement.replace(
        /(<\/h3>|<\/p>)/i,
        ' <span style="color:red">*</span>$1'
      )
      : q?.question_statement;

    // Check if this is a housing option question that should be conditionally shown
    const isHousingDetailQuestion =
      selectedQuestion?.value === 'housing_option' &&
      ['housing_category', 'housing_type', 'preferred_location', 'additional_requirement'].includes(q?.created_for);

    // Get the housing_need answer
    const housingNeedAnswer = selectedQuestion?.value === 'housing_option'
      ? selectedQuestion?.questions?.find(question => question.created_for === 'housing_need')?.answer
      : null;

    // If this is a housing detail question and housing_need is not "yes", don't render it
    if (isHousingDetailQuestion && housingNeedAnswer !== "yes") {
      return null;
    }

    const isHousingDisabled = isAddon && selectedQuestion?.value === "housing_option";

    return (
      <>
        {q?.question_type === "checkbox" && (
          <>
            {selectedQuestion.value === "processing_fee_and_submission" && (
              <>
                {selectedQuestion?.questions[0]?.answer === "ach" ? (
                  <>
                    <div>
                      <h5 style={{ fontWeight: "600" }}>
                        Pay Securely via ACH Bank Transfer
                      </h5>
                      <p>
                        You’ll be redirected to Stripe’s secure checkout to
                        complete your payment.
                      </p>
                      <ul>
                        <li>Have your U.S. bank account info ready</li>
                        <li>
                          Payments are processed securely and may take 3–5
                          business days to clear
                        </li>
                        <li>We never store your bank details</li>
                      </ul>
                    </div>
                    <Divider sx={{ mt: 2, mb: 2 }} />
                  </>
                ) : selectedQuestion?.questions[0]?.answer === "wire" ? (
                  <>
                    <div>
                      <h5 style={{ fontWeight: "600" }}>
                        Pay via Wire Transfer
                      </h5>
                      <p>
                        You’ll be redirected to Stripe’s secure portal for wire
                        transfer instructions.
                      </p>
                      <ul>
                        <li>Easy and secure bank transfer</li>
                        <li>Transfers may take 1–2 business days to process</li>
                      </ul>
                    </div>
                    <Divider sx={{ mt: 2, mb: 2 }} />
                  </>
                ) : selectedQuestion?.questions[0]?.answer === "card" ? (
                  <>
                    <div>
                      <h5 style={{ fontWeight: "600" }}>
                        Pay Securely with Credit Card
                      </h5>
                      <p>
                        You’ll be redirected to Stripe’s secure checkout to
                        complete your payment.
                      </p>
                      <ul>
                        <li>
                          Accepts Visa, MasterCard, American Express, and more
                        </li>
                        <li>Payments are processed instantly</li>
                      </ul>
                    </div>
                    <Divider sx={{ mt: 2, mb: 2 }} />
                  </>
                ) : null}
              </>
            )}
            <FormControl sx={{ mt: 1 }}>
              <p
                className="fw-600 mb-1"
                style={{ lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: questionText }}
              />

              <Box>
                {q?.options.map((opt, i) => (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    key={i}
                    mb={selectedQuestion?.value === "processing_fee_and_submission" ? 2 : 1}
                  >
                    <Checkbox
                      required={isRequired}
                      checked={q?.answer?.includes(opt.value) || false}
                      onChange={() => handleChangeCheckBox(idx, opt.value)}
                      disabled={isHousingDisabled}
                      sx={{
                        color: "#15978a",
                        "&.Mui-checked": { color: "#15978a" },
                      }}
                    />
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          (opt.label ===
                            "I have read, understood, and agree to comply with the Terms and Conditions and Terms of Use of the Little Haiti Global Program."
                            ? `${opt.label} <a href="https://lhgeportal.com/termsofuse" style="color:#15978a;cursor:pointer;" target="_blank">Terms of Use</a>`
                            : opt.label) +
                          (isRequired
                            ? ' <span style="color:red;margin-left:4px;">*</span>'
                            : ""),
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </FormControl>
            <Divider sx={{ mt: 2, mb: 2 }} />
          </>
        )}

        {q?.question_type === "radio" && (
          <div className="col-12">
            <FormControl sx={{ mt: 1 }}>
              <Box display="inline-flex" alignItems="center" mb={1}>
                <div
                  dangerouslySetInnerHTML={{ __html: questionText }}
                />

                {isRequired && (
                  <div
                    style={{
                      color: "red",
                      marginLeft: "4px",
                      display: "inline",
                    }}
                  >
                    *
                  </div>
                )}
              </Box>

              <Box>
                {q?.options.map((opt, i) => (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    key={i}
                    mb={1}
                  >
                    <Radio
                      required={isRequired}
                      checked={q?.answer === opt.value}
                      onChange={() => handleChangeCheckBox(idx, opt.value)}
                      disabled={isHousingDisabled}
                      sx={{
                        color: "#15978a",
                        "&.Mui-checked": { color: "#15978a" },
                      }}
                    />
                    <p
                      className="mb-0 fs-14 fw-500"
                      dangerouslySetInnerHTML={{ __html: opt.label }}
                    />
                  </Box>
                ))}
              </Box>
            </FormControl>
            <Divider sx={{ mt: 2, mb: 2 }} />
            {selectedQuestion?.value === 'housing_option' &&
              q?.created_for === 'housing_need' &&
              q?.answer === 'yes' && (
                <h3
                  className="fw-500 mt-1"
                  style={{ color: '#333' }}
                >
                  Choose Housing Options
                </h3>
              )}

            {selectedQuestion?.value === "housing_option" &&
              q?.created_for === "housing_need" &&
              q?.answer === "yes" &&
              feesData && (
                (() => {
                  const hType = selectedQuestion?.questions?.find(item => item.created_for === "housing_type")?.answer || "";
                  const hCategory = selectedQuestion?.questions?.find(item => item.created_for === "housing_category")?.answer || "";
                  const fee = feesData?.housing_fee_structure?.find(f => f.house_type === hType);
                  const price = fee ? (hCategory === "luxury" ? fee.luxury_price : fee.standard_price) : null;

                  if (!price || !hType || !hCategory) return null;

                  return (
                    <Card
                      sx={{
                        mt: 1,
                        mb: 1,
                        bgcolor: "#f5f9fc",
                        borderLeft: "6px solid #15978a",
                        boxShadow: 1,
                      }}
                    >
                      <CardContent
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 1,
                          px: 1.5,
                          "&:last-child": { pb: 1 },
                        }}
                      >
                        <Box>
                          <span
                            className="fw-600 text-uppercase"
                            style={{
                              color: "#666",
                              fontSize: "0.65rem",
                            }}
                          >
                            Selected Package
                          </span>

                          <h5
                            className="fw-600 mb-0"
                            style={{
                              color: "#333",
                              lineHeight: 1.2,
                            }}
                          >
                            {hType.charAt(0).toUpperCase() + hType.slice(1)} (
                            {hCategory.charAt(0).toUpperCase() + hCategory.slice(1)})
                          </h5>
                        </Box>

                        <Box textAlign="right">
                          <h5
                            className="fw-600 mb-0"
                            style={{ color: "#15978a" }}
                          >
                            ${price}
                          </h5>
                          <span
                            style={{ color: "#999", fontSize: "0.85rem" }}
                          >
                            USD Fee
                          </span>
                        </Box>
                      </CardContent>
                    </Card>

                  );
                })()
              )}
          </div>
        )}
        {q?.question_type === "electronic_sign" && (
          <div className="col-12 d-flex">
            <div
              className="col-md-6 d-flex flex-column"
              style={{ marginTop: "1rem" }}
            >
              <label
                style={{
                  marginBottom: "0.5rem",
                  fontWeight: "bold",
                }}
              >
                {q?.question_statement}
                {q?.is_required && <span style={{ color: "red" }}> *</span>}
              </label>

              {(signaturePreviews[q?.created_for] || q?.answer) &&
                !signatureRefs[q?.created_for]?.isEmpty?.() ? (
                <>
                  <img
                    src={
                      signaturePreviews[q?.created_for] ||
                      `${s3baseUri}${q?.answer}`
                    }
                    alt="Signature"
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      width: isMobile ? "300px" : "100%",
                      height: "150px",
                      objectFit: "contain",
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      signatureRefs[q?.created_for]?.clear?.();
                      handleChangeClear(idx);
                      setSignaturePreviews((prev) => {
                        const updated = { ...prev };
                        delete updated[q?.created_for];
                        return updated;
                      });
                    }}
                    sx={{ mt: 1 }}
                  >
                    Clear & Re-sign
                  </Button>
                </>
              ) : (
                <>
                  <SignatureCanvas
                    penColor="black"
                    canvasProps={{
                      width: 500,
                      height: 150,
                      className: "sigCanvas",
                      style: {
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        width: isMobile ? "300px" : "100%",
                      },
                    }}
                    ref={(ref) => (signatureRefs[q?.created_for] = ref)}
                    onEnd={() =>
                      setSignedFields((prev) => ({
                        ...prev,
                        [q?.created_for]: true,
                      }))
                    }
                  />
                  <div className="text-end d-flex justify-content-end mt-3">
                    <Button
                      variant="outlined"
                      type="button"
                      className="me-3"
                      onClick={() => {
                        signatureRefs[q?.created_for]?.clear?.();
                        handleChangeClear(idx);
                        setSignaturePreviews((prev) => {
                          const updated = { ...prev };
                          delete updated[q?.created_for];
                          return updated;
                        });
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      disabled={!signedFields[q?.created_for]}
                      onClick={() => handleSignatureChange(q?.created_for, idx)}
                    >
                      {signatureLoading ? "Saving..." : "Save Signature"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {q?.question_type === "description" && (
          <div className="col-12 mt-2">
            <Card
              sx={{
                mt: 2,
                mb: 2,
                bgcolor: "#f5f9fc",
                borderLeft: "15px solid #15978a",
                boxShadow: 1,
              }}
            >
              <CardContent
                sx={{ display: "flex", alignItems: "flex-start", p: 2 }}
              >
                <InfoIcon
                  sx={{ color: "#15978a", fontSize: 20, mt: 0.5, mr: 2 }}
                />
                <div
                  style={{ color: "#333" }}
                  dangerouslySetInnerHTML={{ __html: q?.question_statement }}
                />
              </CardContent>
            </Card>
          </div>
        )}
        {q?.question_type === "dropdown" && (
          <div className="col-md-6 mt-4">
            {q?.created_for === "country_of_issue" ||
              q?.created_for === "country_of_residence" ||
              q?.created_for === "nationality_at_birth" ||
              q?.created_for === "place_of_birth" ||
              q?.created_for === "profession_or_occupation" ||
              q?.created_for === "occupation" ||
              q?.created_for === "country" ? (
              <Autocomplete
                fullWidth
                options={q?.options || []}
                getOptionLabel={(option) => option.label}
                value={
                  q?.options.find((opt) => opt.value === q?.answer) || null
                }
                onChange={(e, newValue) => {
                  handleChangeDropdown(idx, newValue?.value || "");
                  setHasUnsavedChanges(true);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={q?.question_statement}
                    required={isRequired}
                    disabled={isHousingDisabled}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password",
                    }}
                  />
                )}
              />
            ) : (
              <FormControl fullWidth required={isRequired}>
                <InputLabel id={`select-label-${idx}`}>
                  {q?.question_statement}
                </InputLabel>
                <Select
                  labelId={`select-label-${idx}`}
                  value={q?.answer}
                  label={q?.question_statement}
                  onChange={(e) => {
                    handleChangeDropdown(idx, e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  disabled={isHousingDisabled}
                >
                  {q?.options?.map((opt, i) => (
                    <MenuItem key={i} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
        )}
        {q?.question_type === "text" && (
          <div className="col-md-6 mt-3">
            {q?.created_for === "mobile_number" ||
              q?.created_for === "other_number" ||
              q?.created_for === "phone_number" ? (
              <div className="position-relative" style={{ marginTop: "9px" }}>
                <div className="small_title">
                  {q?.question_statement.replace(/(<([^>]+)>)/gi, "")}{" "}
                  {q?.is_required ? "*" : ""}
                </div>
                <div>
                  <PhoneInput
                    value={q?.answer || `+${countryCode}`}
                    setValue={() => { }}
                    onChange={(val) => {
                      const updatedQuestion = { ...selectedQuestion };
                      updatedQuestion.questions[idx].answer = val;
                      setSelectedQuestion(updatedQuestion);
                      setHasUnsavedChanges(true);
                    }}
                    disabled={isHousingDisabled}
                    placeholder=" "
                    inputClass="phone_input"
                    autoSelectCountry={true}
                    enableSearch={true}
                    countryCodeEditable={false}
                    required={q?.is_required}
                  />
                </div>
              </div>
            ) : (
              <TextField
                required={q?.is_required}
                label={q?.question_statement.replace(/(<([^>]+)>)/gi, "")}
                fullWidth
                type={q?.type}
                variant="outlined"
                value={q?.answer || ""}
                InputLabelProps={{
                  shrink: Boolean(q?.answer),
                }}
                inputProps={{
                  autoComplete: "new-password",
                }}
                onChange={(e) => {
                  let val = e.target.value;

                  if (q?.created_for?.toLowerCase().includes("name")) {
                    val = val.replace(/[^A-Za-z\s]/g, "");
                  }
                  const updatedQuestion = { ...selectedQuestion };
                  updatedQuestion.questions[idx].answer = val;
                  setSelectedQuestion(updatedQuestion);
                  setHasUnsavedChanges(true);
                }}
                disabled={isHousingDisabled}
                sx={{ mt: 1 }}
              />
            )}
          </div>
        )}
        {q?.question_type === "boxwithcheck" && (
          <div className="col-12">
            <Box
              sx={{
                mt: 2,
                // mb:2,
                display: "flex",
                alignItems: "center",
                bgcolor: "#f9f9f9",
                border: "1px solid #ddd",
                borderRadius: 1,
                p: 2,
              }}
            >
              <Checkbox
                checked={!!q?.answer}
                onChange={(e) => {
                  const updatedQuestion = { ...selectedQuestion };
                  updatedQuestion.questions[idx].answer = e.target.checked;
                  setSelectedQuestion(updatedQuestion);
                  setHasUnsavedChanges(true);
                }}
                required={q?.is_required}
                disabled={isHousingDisabled}
                sx={{ mt: 0.5 }}
              />
              <p
                className="mb-0 fs-14"
                style={{ color: "#333", lineHeight: 1.6, marginLeft: "8px" }}
              >
                <span dangerouslySetInnerHTML={{ __html: q?.question_statement }} />
                {isRequired && (
                  <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                )}
              </p>
            </Box>
          </div>
        )}
        {q?.question_type === "date" && (
          <div className="col-md-6 mt-4 ">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                className="date-picker"
                label={q?.question_statement}
                // value={q?.answer ? new Date(q?.answer) : null}
                value={
                  q?.answer ? moment(q?.answer, "YYYY-MM-DD").toDate() : null
                }
                disableFuture={
                  q?.created_for === "date_of_birth" ||
                  q?.created_for === "date_of_issue"
                }
                onChange={(newValue) => handleDateChange(newValue, q, idx)}
                disabled={isHousingDisabled}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: q?.is_required,
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        )}
        {q?.question_type === "file" &&
          (q?.created_for !== "dcpj_data_page" || checkcountry) ? (
          <div className="col-12">
            <Box sx={{ mb: 2, mt: 2 }}>
              <p
                className="fw-600 mb-2"
              >
                <>{q?.question_statement}</>
                {q?.is_required &&
                  !(
                    q?.created_for === "dcpj_data_page" &&
                    gettingStartedTitle === "g100"
                  ) && <span style={{ color: "red", marginLeft: 4 }}>*</span>}
              </p>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
                sx={{
                  border: "2px dotted #15978a",
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: "#f5f9fc",
                }}
              >
                <Box flex={1}>
                  {q?.created_for === "dcpj_data_page" &&
                    checkcountry ? (
                    <p className="mb-0">
                      Please upload a valid <strong>DCPJ certificate</strong>{" "}
                      issued within the last 60 days. <br />
                      Please upload up to 5 files (images or documents)
                      <br />
                      <span style={{ fontSize: "14px" }}>
                        Max file size: 5MB per file.
                      </span>
                    </p>
                  ) : (
                    <p className="mb-0 fs-14 text-secondary">
                      {q?.description ||
                        "Please upload up to 5 files (images or documents)"}
                      <br />
                      <span style={{ fontSize: "14px" }}>
                        Max file size: 5MB per file.
                      </span>
                    </p>
                  )}

                  {(!q?.answer || q?.answer.length === 0) &&
                    ["passport_size_photograph", "signature_image"].includes(
                      q?.created_for
                    ) && (
                      <Box
                        display="flex"
                        alignItems="center"
                        mt={1}
                        color="gray"
                      >
                        <ImageIcon sx={{ mr: 1 }} />
                        <span style={{ fontSize: "14px", color: "gray" }}>
                          Please upload clear images.
                        </span>
                      </Box>
                    )}
                </Box>

                {q?.answer && q?.answer.length > 0 && (
                  <Box sx={{ width: "100%" }}>
                    {q?.answer.map((file, index) => (
                      <Box
                        key={index}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          maxWidth: "300px",
                          overflow: "hidden",
                          mb: 1,
                        }}
                      >
                        <a
                          href={s3baseUri + file?.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: "none",
                            color: "#1976d2",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 200,
                            display: "inline-block",
                          }}
                          title={file.name}
                        >
                          <p
                            className="mb-0 fs-14 fw-500"
                            style={{
                              cursor: "pointer",
                            }}
                          >
                            {file.name}
                          </p>
                        </a>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => removeFile(q, index)}
                          sx={{ minWidth: 0 }}
                        >
                          ✕
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}

                <Button
                  className="mui-button"
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  sx={{ whiteSpace: "nowrap" }}
                  disabled={
                    uploadingFile === idx ||
                    (q?.answer && q?.answer.length >= 5)
                  }
                >
                  {uploadingFile === idx
                    ? "Uploading...."
                    : q?.answer && q?.answer.length >= 5
                      ? "Max 5 files uploaded"
                      : "Upload Files"}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=".pdf,.jpg,.jpeg"
                    onChange={async (e) => {
                      if (!e.target.files.length) return;

                      const existingCount = q?.answer ? q?.answer.length : 0;
                      const maxAllowed = 5;
                      const canUploadCount = maxAllowed - existingCount;

                      if (canUploadCount <= 0) {
                        enqueueSnackbar("Maximum 5 files allowed", {
                          variant: "warning",
                        });
                        e.target.value = null;
                        return;
                      }

                      const filesToUpload = Array.from(e.target.files).slice(
                        0,
                        canUploadCount
                      );

                      const allowedTypes = [
                        "application/pdf",
                        "image/jpeg",
                        "image/jpg",
                      ];
                      const invalidFiles = filesToUpload.filter(
                        (file) => !allowedTypes.includes(file.type)
                      );

                      if (invalidFiles.length > 0) {
                        enqueueSnackbar(
                          `Only PDF and JPEG files are allowed.`,
                          { variant: "error" }
                        );
                        e.target.value = null;
                        return;
                      }

                      const uploadedPaths = [];

                      for (const file of filesToUpload) {
                        const sizeInMB = file.size / (1024 * 1024);
                        const roundedSize = Math.round(sizeInMB * 100) / 100;

                        if (roundedSize > 5) {
                          enqueueSnackbar(
                            `This file is larger than 5MB. Please upload a smaller file.`,
                            {
                              variant: "error",
                            }
                          );
                          return;
                        }

                        try {
                          const formData = new FormData();
                          formData.append("file", file);
                          setUploadingFile(idx);
                          const res = await image_upload_on_s3_api(formData);
                          if (res.code === 200) {
                            uploadedPaths.push({
                              name: file.name,
                              path: res.path,
                              type: file.type,
                              size: file.size,
                            });
                            setUploadingFile(null);
                          } else {
                            enqueueSnackbar(`Failed to upload ${file.name}`, {
                              variant: "error",
                            });
                            setUploadingFile(null);
                          }
                        } catch {
                          enqueueSnackbar(`Failed to upload ${file.name}`, {
                            variant: "error",
                          });
                        }
                      }

                      setSelectedQuestion((prev) => {
                        const updatedQuestions = prev.questions.map(
                          (question) => {
                            if (question.created_for === q?.created_for) {
                              return {
                                ...question,
                                answer: [
                                  ...(question.answer || []),
                                  ...uploadedPaths,
                                ],
                              };
                            }
                            return question;
                          }
                        );
                        return { ...prev, questions: updatedQuestions };
                      });

                      setUploadingFile(null);
                      e.target.value = null;
                      setHasUnsavedChanges(true);
                    }}
                  />
                </Button>
              </Box>

              {q?.answer && q?.answer.length > 0 && (
                <span className="fs-12 text-secondary">
                  {q?.answer.length}/5 files uploaded
                </span>
              )}
            </Box>
            <Divider sx={{ mt: 2, mb: 2, width: "100%" }} />
          </div>
        ) : ("")}
        {q?.question_type === "statement" && (
          <div className="col-12 mt-2">
            <div>
              <ul style={{ paddingLeft: "16px", margin: 0 }}>
                <li
                  style={{ color: "#333", lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={{ __html: q?.question_statement }}
                />
              </ul>
            </div>
          </div>
        )}

        {q?.question_type === "view_file" && (
          <div className="col-12 mb-3">
            <div className="card shadow-sm rounded-3">
              <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="card-title mb-1">{q?.question_statement}</h5>
                </div>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DescriptionIcon />}
                  type="button"
                  onClick={() => {
                    window.open(s3baseUri + q?.answer, "_blank");
                  }}
                >
                  View
                </Button>
              </div>
            </div>
          </div>
        )}

        {q?.question_type === "heading" && (
          <div className="col-12 mt-4 mb-2">
            {selectedQuestion?.value === "visa_type_and_application_details" ? (
              <h3 style={{ marginBottom: "0.5rem", fontWeight: "600", color: "#333" }}>
                {q?.question_statement}
              </h3>
            ) : (
              <label style={{ marginBottom: "0.5rem", fontWeight: "bold" }}>
                {q?.question_statement}
              </label>
            )}
          </div>
        )}
      </>

    );

  };

  useEffect(() => {
    const initializeForm = async () => {
      const formData = await handleFetchDetail();
      if (formData) {
        handleSetSelectedQuestion(formData);
      }
      await getDocuments();
    };
    initializeForm();
  }, []);

  useEffect(() => {
    if (sectionList && sectionList.length > 0) {
      handleSetSelectedQuestion(sectionList);
    }
  }, [pathname]);

  const blockIfYes = [
    "criminal_record",
    "criminal_record_three",
    "criminal_record_four",
    "criminal_record_five",
    "visa_refusal_deportation",
    "visa_refusal_criminal_record",
  ];

  const shouldDisableButton =
    selectedQuestion?.value === "criminal_record" &&
    selectedQuestion?.questions?.some(
      (q) =>
        blockIfYes?.includes(q?.created_for) &&
        q?.answer?.toLowerCase() === "yes"
    );

  const checkcountry =
    sectionList?.find((q) => q?.value === "visa_type_and_application_details")
      ?.questions?.find(q => q.created_for === "country_of_residence")?.answer === "haiti";

  const sectionsToCheck = [
    "visa_type_and_application_details",
    "required_documentation",
    "certification",
    "processing_fee_and_submission",
  ];

  let isButtonDisabled = shouldDisableButton;

  if (sectionsToCheck.includes(selectedQuestion?.value)) {
    const isCurrentSectionCompleted = selectedQuestion?.questions?.every((q) => {
      // Skip validation for description or heading types
      if (q.question_type === "description" || q.question_type === "heading" || q.question_type === "statement") {
        return true;
      }

      if (!q.is_required) return true;

      // Special handling for dcpj_data_page
      if (q.created_for === "dcpj_data_page" && !checkcountry) return true;

      if (q.question_type === "file") {
        return Array.isArray(q.answer) && q.answer.length > 0;
      }

      if (q.question_type === "checkbox") {
        // For Acknowledgment section, ensure ALL options are checked
        if (selectedQuestion?.value === "processing_fee_and_submission") {
          return Array.isArray(q.answer) && q.answer.length === q.options.length;
        }
        return Array.isArray(q.answer) && q.answer.length > 0;
      }

      if (q.question_type === "boxwithcheck") {
        return q.answer === true;
      }

      if (q.question_type === "electronic_sign") {
        return !!q.answer;
      }

      return !!q.answer;
    });

    if (!isCurrentSectionCompleted) {
      isButtonDisabled = true;
    }
  }

  const rules = {
    getting_started: ["eligibility_certification_statement"],
    background_check_disclosures: [
      "acknowledge_documents",
      "consent_background_check",
    ],
  };

  let disable = false;

  if (selectedQuestion?.value && rules[selectedQuestion.value]) {
    disable = rules[selectedQuestion.value].some((field) => {
      return (
        selectedQuestion?.questions?.find((q) => q.created_for === field)
          ?.answer === "no"
      );
    });
  }

  const is_disable = disable;

  const gettingStartedTitle = sectionList?.find(
    (q) => q?.value === "visa_type_and_application_details"
  )?.sub_sections?.[0]?.questions?.[0]?.answer;

  let question_statement = `<p>After successful payment, the application will be submitted. A confirmation message will appear with the next steps, including the $10,000.00 USD application processing fee due upon approval.</p>  <p> When this subsection is selected on the right, we will show the Application processing fee ($1,500.00 USD) and three payment methods (Stripe, ACH, and Wire).The user can select one payment method using the radio button.</p><p>Applicants need to pay the processing fee before they can submit the form. Once the fee is paid, the user will be able to submit the form. Once the form is submitted, we will show a message confirming their submission and that if your application is approved, you will have to pay the application processing fee as soon as your application is approved.</p><p>As soon as a user has completed and submitted the application. Now, before a user moves to transfer the payment, we need to show this message related to the Refund policy.</p>`;

  const questionArray = selectedQuestion?.has_sub_sections
    ? selectedQuestion?.sub_sections
    : selectedQuestion?.questions;

  const dynamicTitle = selectedQuestion?.title;

  if (initialLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <>
      <form
        onSubmit={(e) => {
          handleSave(e, gettingStartedTitle == "g200" ? true : false);
        }}
      >
        <div className="row">
          <div className="col-12">
            <h2 className="mb-3">
              {dynamicTitle}
            </h2>

            {selectedQuestion?.value === "getting_started" && (
              <>
                {isAddon && addonInfo && (
                  <Card
                    sx={{
                      display: "inline-block",
                      maxWidth: "fit-content",
                      mb: 3,
                      mt: 2,
                      bgcolor: "#f0f7f7",
                      borderLeft: "8px solid #15978a",
                      borderRadius: "12px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      overflow: "hidden",
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box display="flex" alignItems="center" mb={1.5} justifyContent="space-between" width="100%">
                        <Box display="flex" alignItems="center">
                          <h3 className="mb-0" style={{ color: "#15978a" }}>
                            Add-on Application
                          </h3>
                        </Box>
                        <Tooltip title="Edit Add-on">
                          <IconButton
                            type="button"
                            size="small"
                            onClick={() => setOpenAddonDialog(true)}
                            sx={{
                              color: "#15978a",
                              bgcolor: "rgba(21, 151, 138, 0.1)",
                              "&:hover": { bgcolor: "rgba(21, 151, 138, 0.2)" },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Divider sx={{ mb: 2, opacity: 0.6 }} />
                      <Box display="flex" flexWrap="wrap" gap={4}>
                        <Box>
                          <span
                            style={{
                              color: "#666",
                              display: "block",
                              mb: 0.5,
                              fontWeight: "600",
                              textTransform: "uppercase",
                              fontSize: "0.75rem"
                            }}
                          >
                            Full Name
                          </span>
                          <p
                            className="fw-600 mb-0"
                            style={{ color: "#333" }}
                          >
                            {addonInfo.first_name} {addonInfo.last_name}
                          </p>
                        </Box>
                        <Box>
                          <span
                            style={{
                              color: "#666",
                              display: "block",
                              mb: 0.5,
                              fontWeight: "600",
                              textTransform: "uppercase",
                              fontSize: "0.75rem"
                            }}
                          >
                            Email Address
                          </span>
                          <p
                            className="fw-600 mb-0"
                            style={{ color: "#333" }}
                          >
                            {addonInfo.email}
                          </p>
                        </Box>
                        <Box>
                          <span
                            style={{
                              color: "#666",
                              display: "block",
                              mb: 0.5,
                              fontWeight: "600",
                              textTransform: "uppercase",
                              fontSize: "0.75rem"
                            }}
                          >
                            Relationship
                          </span>
                          <p
                            className="fw-600 mb-0"
                            style={{ color: "#333" }}
                          >
                            {addonInfo.relationship}
                          </p>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                <h3 className="mb-3">
                  Eligibility Certification Statement
                </h3>
              </>
            )}
            {selectedQuestion?.intro_text && (
              <p>{selectedQuestion?.intro_text}</p>
            )}
          </div>
          {isAddon && selectedQuestion?.value === "housing_option" && (
            <div className="col-12 mb-3">
              <Alert severity="info" sx={{ borderRadius: "8px" }}>
                Primary applicant already has housing option chosen, please
                continue to next step.
              </Alert>
            </div>
          )}
          {section === "application_review_and_certification" && (
            <div className="col-12">
              <ReviewGettingStartedSection />
            </div>
          )}
          {selectedQuestion?.value === "processing_fee_and_submission" &&
            isPaid === "paid" ? (
            <div className="col-12 mt-2">
              <Card
                sx={{
                  mt: 2,
                  mb: 2,
                  bgcolor: "#e6f4ea",
                  borderLeft: "15px solid #2e7d32",
                  boxShadow: 1,
                }}
              >
                <CardContent sx={{ display: "flex", alignItems: "center", p: 2 }}>
                  <CheckCircleIcon
                    sx={{ color: "#2e7d32", fontSize: 20, mr: 2 }}
                  />
                  <div style={{ color: "#2e7d32" }}>
                    Payment has already been completed.
                  </div>
                </CardContent>
              </Card>
              <Card
                sx={{
                  mt: 2,
                  mb: 2,
                  bgcolor: "#f5f9fc",
                  borderLeft: "15px solid #15978a",
                  boxShadow: 1,
                }}
              >
                <CardContent
                  sx={{ display: "flex", alignItems: "flex-start", p: 2 }}
                >
                  <InfoIcon
                    sx={{ color: "#15978a", fontSize: 20, mt: 0.5, mr: 2 }}
                  />
                  <div
                    style={{ color: "#333" }}
                    dangerouslySetInnerHTML={{ __html: question_statement }}
                  />
                </CardContent>
              </Card>
            </div>
          ) : (
            <>
              {selectedQuestion?.value === "background_check_disclosures" && (
                (!documentFiles || documentFiles.length === 0) && (
                  <div className="col-12 mb-3">
                    <Alert severity="info"
                      sx={{
                        backgroundColor: "#f2f2f2ff",
                        color: "#666",
                        "& .MuiAlert-icon": {
                          color: "#999",
                        },
                      }}>
                      No Document Found
                    </Alert>
                  </div>
                )
              )}

              <>
                {questionArray?.map((q, idx) => renderQuestion(q, idx))}</>
            </>
          )}

        </div>

        {shouldDisableButton && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <i className="fas fa-eye-slash" style={{ marginRight: 8 }}></i>
            Based on your answers, you are not eligible to proceed further.
          </Alert>
        )}

        {!checkStatus && (
          <Box display="flex" justifyContent="flex-end" mt={2}>
            {is_disable ? (
              <Tooltip
                title="You cannot proceed until you select 'Yes'"
                placement="top"
                arrow
              >
                <span>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled
                  >
                    {selectedQuestion?.value ===
                      "processing_fee_and_submission" && isPaid === "paid"
                      ? "Submit"
                      : isLoading
                        ? "Saving..."
                        : "Save And Continue"}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isButtonDisabled || isLoading}
                >
                  {selectedQuestion?.value === "processing_fee_and_submission" &&
                    isPaid === "paid"
                    ? "Submit"
                    : isLoading
                      ? "Saving..."
                      : "Save And Continue"}
                </Button>
              </>
            )}
          </Box>
        )}
      </form>
      <UpdateAddonDialog
        open={openAddonDialog}
        onClose={() => setOpenAddonDialog(false)}
        getDetail={handleFetchDetail}
        initialData={addonInfo}
        addonId={addon_id}
      />
    </>
  );
};
